import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import {
  Bell, Clock, Check, X, Loader2, Building2, ChevronDown,
  AlertTriangle, ArrowRight,
} from "lucide-react";
import InvestorPortalDisclaimer from "@/components/investor-portal-disclaimer";

const BASE = import.meta.env.BASE_URL;

interface RofrItem {
  id: number;
  companyId: number;
  companyName: string;
  companySector: string | null;
  roundName: string;
  roundSize: string | null;
  valuation: string | null;
  leadInvestor: string | null;
  terms: string | null;
  deadline: string | null;
  status: string;
  notes: string | null;
  myResponse: { response: string; notes: string | null; respondedAt: string } | null;
}

function daysUntil(date: string): number {
  return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function InvestorPortalRofr() {
  const [, navigate] = useLocation();
  const [loading, setLoading] = useState(true);
  const [rofrs, setRofrs] = useState<RofrItem[]>([]);
  const [responding, setResponding] = useState<number | null>(null);

  const fetchRofrs = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) { navigate("/investor/login"); return; }

    try {
      const res = await fetch(`${BASE}api/investor-portal/rofr`, {
        headers: { "x-investor-token": token },
      });
      if (res.status === 401) { navigate("/investor/login"); return; }
      if (res.ok) setRofrs(await res.json());
    } catch {}
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchRofrs(); }, [fetchRofrs]);

  const handleRespond = async (rofrId: number, response: "interested" | "pass") => {
    setResponding(rofrId);
    const token = localStorage.getItem("investor_token");
    if (!token) return;

    try {
      const res = await fetch(`${BASE}api/investor-portal/rofr/${rofrId}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-investor-token": token },
        body: JSON.stringify({ response }),
      });
      if (res.ok) fetchRofrs();
    } catch {}
    setResponding(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-[#FF5C00] animate-spin" />
    </div>
  );

  const active = rofrs.filter(r => r.status === "active");
  const past = rofrs.filter(r => r.status !== "active");
  const pending = active.filter(r => !r.myResponse);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold font-serif tracking-tight text-white flex items-center gap-2">
          <Bell className="w-6 h-6 text-[#FF5C00]" /> ROFR Center
        </h1>
        <p className="text-white/40 text-sm mt-1">Right of First Refusal opportunities for your portfolio companies</p>
      </div>

      {pending.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <p className="text-amber-400 text-sm font-medium">{pending.length} opportunity{pending.length > 1 ? "ies" : ""} awaiting your response</p>
        </div>
      )}

      {active.length === 0 && past.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-12 text-center">
          <Bell className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-white/40">No ROFR opportunities yet</p>
          <p className="text-white/20 text-sm mt-1">You'll be notified when any portfolio company raises a new round</p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div>
              <h2 className="text-lg font-bold font-serif text-white mb-4">Active Opportunities</h2>
              <div className="space-y-4">
                {active.map(rofr => (
                  <div key={rofr.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-white font-medium">{rofr.companyName}</h3>
                        <p className="text-[#FF5C00] text-sm font-medium mt-0.5">{rofr.roundName}</p>
                      </div>
                      {rofr.deadline && (
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                          daysUntil(rofr.deadline) <= 7 ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"
                        }`}>
                          <Clock className="w-3 h-3" />
                          {daysUntil(rofr.deadline)} days left
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {rofr.roundSize && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-white/30 text-[10px] uppercase tracking-wider">Round Size</p>
                          <p className="text-white font-medium text-sm mt-0.5">{rofr.roundSize}</p>
                        </div>
                      )}
                      {rofr.valuation && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-white/30 text-[10px] uppercase tracking-wider">Valuation</p>
                          <p className="text-white font-medium text-sm mt-0.5">{rofr.valuation}</p>
                        </div>
                      )}
                      {rofr.leadInvestor && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-white/30 text-[10px] uppercase tracking-wider">Lead Investor</p>
                          <p className="text-white font-medium text-sm mt-0.5">{rofr.leadInvestor}</p>
                        </div>
                      )}
                      {rofr.deadline && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-white/30 text-[10px] uppercase tracking-wider">Deadline</p>
                          <p className="text-white font-medium text-sm mt-0.5">{new Date(rofr.deadline).toLocaleDateString("en-IN")}</p>
                        </div>
                      )}
                    </div>

                    {rofr.terms && <p className="text-white/40 text-xs mb-4">{rofr.terms}</p>}

                    {rofr.myResponse ? (
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                        rofr.myResponse.response === "interested"
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "bg-white/5 text-white/40 border border-white/10"
                      }`}>
                        {rofr.myResponse.response === "interested" ? (
                          <><Check className="w-4 h-4" /> Interested — Response recorded</>
                        ) : (
                          <><X className="w-4 h-4" /> Passed</>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleRespond(rofr.id, "interested")}
                          disabled={responding === rofr.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                        >
                          {responding === rofr.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          I'm Interested
                        </button>
                        <button
                          onClick={() => handleRespond(rofr.id, "pass")}
                          disabled={responding === rofr.id}
                          className="bg-white/5 hover:bg-white/10 text-white/60 px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors border border-white/10"
                        >
                          <X className="w-4 h-4" /> Pass
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h2 className="text-lg font-bold font-serif text-white mb-4 text-white/60">Past Opportunities</h2>
              <div className="space-y-3">
                {past.map(rofr => (
                  <div key={rofr.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 opacity-60">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{rofr.companyName} — {rofr.roundName}</p>
                        <p className="text-white/30 text-xs mt-0.5">
                          {rofr.roundSize && `${rofr.roundSize} `}
                          {rofr.valuation && `@ ${rofr.valuation}`}
                        </p>
                      </div>
                      {rofr.myResponse && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          rofr.myResponse.response === "interested" ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-white/30"
                        }`}>
                          {rofr.myResponse.response}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <InvestorPortalDisclaimer />
    </div>
  );
}
